// apps/web/src/features/clients/components/LegalEntityFormFields.tsx
//
// Rows shared by the add and edit legal-entity forms. The two forms hold
// different field sets (add carries `type` + the first requisites text, edit
// only the identity), so the rows are generic over the form shape rather than
// bound to one concrete type.
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH, ClientLegalEntityType } from '@magimp/shared';
import { Banner, FormRow, Input } from '../../../ui/index.js';
import type { CSSProperties } from 'react';

interface SharedFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  error: string | undefined;
  fieldStyle: CSSProperties;
}

export function LegalNameField<T extends FieldValues>({
  control,
  name,
  error,
  fieldStyle,
}: SharedFieldProps<T>) {
  const { t } = useTranslation(['clients']);
  return (
    <FormRow label={t('clients:fields.legalName')} error={error}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            ref={field.ref}
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            invalid={!!error}
            maxLength={CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH}
            style={fieldStyle}
          />
        )}
      />
    </FormRow>
  );
}

export function LegalInnField<T extends FieldValues>({
  control,
  name,
  error,
  type,
  fieldStyle,
}: SharedFieldProps<T> & { type: ClientLegalEntityType }) {
  const { t } = useTranslation(['clients']);
  return (
    <FormRow
      label={t('clients:fields.inn')}
      // The digit count is bound to the legal form; saying so up front beats a
      // checksum error after the fact.
      hint={
        type === ClientLegalEntityType.OOO ? t('clients:hints.innOoo') : t('clients:hints.innIp')
      }
      error={error}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            ref={field.ref}
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            invalid={!!error}
            style={fieldStyle}
          />
        )}
      />
    </FormRow>
  );
}

export function LegalSubmitErrorBanner({
  state,
}: {
  state: { text: string | null; uncertain: boolean };
}) {
  if (state.text === null) return null;
  return (
    <div style={{ marginBottom: 'var(--s-3)' }}>
      <Banner tone={state.uncertain ? 'warn' : 'error'}>{state.text}</Banner>
    </div>
  );
}
